import CustomSearch, { ISearchElement } from 'components/CustomSearch/CustomSearch';
import styles from 'assets/jss/material-dashboard-pro-react/views/users/addUserDialogStyle';
import { makeStyles } from '@material-ui/core/styles';
import CustomTag from 'components/CustomTag/CustomTag';
import { translate } from 'components/Translation';
import { BodyM } from 'components/Typography/Typography';
import React, { useEffect, useMemo, useState } from 'react';
import { finalize } from 'rxjs/operators';
import { BillsService } from 'services/bills/bills';
import { SingleBillId } from 'utils/api/models/bills/singleBillId';
import CustomCheckbox from 'components/CustomCheckbox/CustomCheckbox';
import CustomDatePicker from 'components/CustomDatePicker/CustomDatePicker';
import moment from 'moment';
import { isValidDate } from 'utils/date/validateDate';

// @ts-ignore
const useStyles = makeStyles(styles);

const BillingAccountsTab = ({
	selectedBillingIds,
	setSelectedBillingIds,
	billingAccountExpirationDate,
	setBillingAccountExpirationDate,
	billingAccountUnlimitedExpirationDate,
	setBillingAccountUnlimitedExpirationDate,
}: {
	selectedBillingIds: string[];
	setSelectedBillingIds: React.Dispatch<React.SetStateAction<string[]>>;
	billingAccountExpirationDate: moment.Moment | null;
	setBillingAccountExpirationDate: React.Dispatch<React.SetStateAction<moment.Moment | null>>;
	billingAccountUnlimitedExpirationDate: boolean;
	setBillingAccountUnlimitedExpirationDate: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
	const classes = useStyles();
	const [loading, setLoading] = useState(false);
	const [billingIds, setBillingIds] = useState<SingleBillId[]>([]);

	useEffect(() => {
		setLoading(true);
		const subscription = BillsService.getCorpBillingIds()
			.pipe(finalize(() => setLoading(false)))
			.subscribe(({ billingIds }) => {
				setBillingIds(billingIds);
			});
		return () => {
			subscription.unsubscribe();
		};
	}, []);

	const mappedBillingIdNumbers = useMemo(() => {
		return [...new Set(billingIds.map((billId) => billId.billingId))]
			.filter((billingId) => {
				return !selectedBillingIds.includes(billingId);
			})
			.map((billingId) => ({
				id: billingId,
				value: billingId,
				content: billingId,
			}));
	}, [billingIds, selectedBillingIds]);

	const onSuggestionClick = (suggestion: ISearchElement | null) => {
		if (suggestion) {
			setSelectedBillingIds((billingIds) => billingIds.concat([suggestion.value]));
		}
	};

	const removeFromBillingIds = (removedBillingId: string) => {
		setSelectedBillingIds((oldBillingIds) =>
			oldBillingIds.filter((oldBillingId) => oldBillingId !== removedBillingId)
		);
	};

	return (
		<div className={classes.expirationDateWrapper}>
			<BodyM>{translate('select.expiration.date')}</BodyM>
			<div>
				<CustomDatePicker
					timeFormat={false}
					value={billingAccountExpirationDate}
					onChange={(date) => {
						if (moment.isMoment(date)) {
							setBillingAccountExpirationDate(date);
							setBillingAccountUnlimitedExpirationDate(false);
						}
					}}
					inputProps={{
						placeholder: translate('department.details.expiration.date'),
					}}
					isValidDate={(currentDate) => isValidDate(currentDate, 'yesterday')}
				/>
				<CustomCheckbox
					label={translate('unlimited')}
					checked={billingAccountUnlimitedExpirationDate}
					onClick={() => {
						setBillingAccountUnlimitedExpirationDate((value: boolean) => !value);
						setBillingAccountExpirationDate(null);
					}}
				/>
			</div>
			<br />
			<BodyM>{translate('users.permissions.billing.account.create.user.reason')}</BodyM>
			<CustomSearch
				elements={mappedBillingIdNumbers}
				id="billingIdSearch"
				label={''}
				placeholder={translate('users.permissions.billing.account.create.user.select')}
				elementSelected={onSuggestionClick}
				selectedElement={null}
				loading={loading}
			/>
			<div>
				{selectedBillingIds.map((billingId) => (
					<div key={billingId} className={classes.customNumber}>
						<CustomTag
							className={`custom-number-tag ${classes.numberTag}`}
							onClick={() => removeFromBillingIds(billingId)}
							label={billingId}
						/>
					</div>
				))}
			</div>
		</div>
	);
};

export default BillingAccountsTab;
